/**
 * WordPress dependencies
 */
import { useDispatch } from '@wordpress/data';
import {
	store as blockEditorStore,
	__experimentalBlockPatternsList as BlockPatternsList,
	BlockContextProvider,
} from '@wordpress/block-editor';
import { Modal, SearchControl, Button, Spinner } from '@wordpress/components';
import { __, sprintf } from '@wordpress/i18n';
import { useState, Fragment, RawHTML } from '@wordpress/element';
import { useEntityRecords } from '@wordpress/core-data';
import { parse } from '@wordpress/blocks';
import { lock } from '@wordpress/icons';

function PatternCategoriesList( {
	selectedCategory,
	patternCategories,
	onClickCategory,
} ) {
	const baseClassName = 'block-editor-block-patterns-explorer__sidebar';
	return (
		<div className={ `${ baseClassName }__categories-list` }>
			{ [ ...new Set( patternCategories ) ].map( ( category ) => {
				return (
					<Button
						key={ category.slug }
						label={ category.name }
						className={ `${ baseClassName }__categories-list__item` }
						isPressed={ selectedCategory === category.slug }
						onClick={ () => {
							onClickCategory( category.slug );
						} }
					>
						{ category.name }
					</Button>
				);
			} ) }
		</div>
	);
}

function PatternsExplorerSearch( { searchValue, setSearchValue } ) {
	const baseClassName = 'block-editor-block-patterns-explorer__search';
	return (
		<div className={ baseClassName }>
			<SearchControl
				__nextHasNoMarginBottom
				onChange={ setSearchValue }
				value={ searchValue }
				label={ __( 'Search for patterns' ) }
				placeholder={ __( 'Search' ) }
			/>
		</div>
	);
}

function PatternExplorerSidebar( {
	selectedCategory,
	patternCategories,
	onClickCategory,
	searchValue,
	setSearchValue,
} ) {
	const baseClassName = 'block-editor-block-patterns-explorer__sidebar';
	return (
		<div className={ baseClassName }>
			<PatternsExplorerSearch
				searchValue={ searchValue }
				setSearchValue={ setSearchValue }
			/>
			{ ! searchValue && (
				<PatternCategoriesList
					selectedCategory={ selectedCategory }
					patternCategories={ patternCategories }
					onClickCategory={ onClickCategory }
				/>
			) }
		</div>
	);
}

const PatternsList = ( { clientId, shownPatterns, onRequestClose } ) => {
	const [ show, setShow ] = useState( false );
	const { updateBlockAttributes, replaceInnerBlocks } =
		useDispatch( blockEditorStore );

	const onBlockPatternSelect = ( pattern ) => {
		if ( pattern.pro && ! window.popper.pro ) {
			setShow( true );
		} else {
			const { id, uuid, ...attrs } = pattern.blocks[ 0 ].attributes;

			updateBlockAttributes( clientId, {
				...attrs,
				backgroundColor: null,
			} );
			replaceInnerBlocks(
				clientId,
				pattern.blocks[ 0 ].innerBlocks,
				true
			);
			onRequestClose();
		}
	};

	return (
		<Fragment>
			{ show && (
				<Modal
					title={ __( 'PRO feature', 'popper' ) }
					onRequestClose={ () => setShow( false ) }
				>
					<RawHTML>
						{ sprintf(
							/* translators: Link to site. */
							// eslint-disable-next-line @wordpress/i18n-no-collapsible-whitespace, @wordpress/i18n-translator-comments
							__(
								`<p>Sorry, this template is a PRO feature. With %s you can add mail services integrations <br />
								(Mailchimp, ConvertKit, MailPoet, GetResponse and more…) to your popups.</p>
								<p>You can also send data to Google Sheets or to webhooks and more.</p>
								<p>Please upgrade to the PRO plan to unlock all these awesome features.</p>`,
								'popper'
							),
							`<a href="https://www.popperwp.com/" target="_blank">Popper Pro</a>`
						) }
					</RawHTML>
					<Button
						variant="primary"
						href="https://www.popperwp.com/"
						icon={ lock }
						iconPosition={ 'left' }
						target="_blank"
					>
						{ __( 'Upgrade to PRO', 'popper' ) }
					</Button>
				</Modal>
			) }

			<BlockPatternsList
				blockPatterns={ shownPatterns }
				shownPatterns={ shownPatterns }
				onClickPattern={ onBlockPatternSelect }
			/>
		</Fragment>
	);
};

export function TemplatesModal( { clientId, onRequestClose } ) {
	const [ searchValue, setSearchValue ] = useState();
	const [ selectedCategory, setSelectedCategory ] = useState( 'all' );

	const { records: patterns } = useEntityRecords( 'popper/v1', 'patterns', {
		per_page: -1,
	} );

	const shownPatterns = patterns
		?.map( ( p ) => {
			p.blocks = parse( p.content );
			return p;
		} )
		.filter( ( p ) => {
			if ( searchValue ) {
				return p.categories.includes( searchValue );
			}
			if ( 'all' === selectedCategory ) {
				return true;
			}
			return p.categories.includes( selectedCategory );
		} );

	const patternCategories = [
		{
			slug: 'all',
			name: __( 'All', 'popper' ),
		},
		{
			slug: 'promo',
			name: __( 'Promo', 'popper' ),
		},
		{
			slug: 'stickybar',
			name: __( 'Sticky Bar', 'popper' ),
		},
		{
			slug: 'fullscreen',
			name: __( 'Full screen', 'popper' ),
		},
		{
			slug: 'adblock',
			name: __( 'Ad Block', 'popper' ),
		},
	];

	return (
		<Modal
			className="block-editor-query-pattern__selection-modal"
			isFullScreen
			title={ __( 'Choose a pattern' ) }
			closeLabel={ __( 'Cancel' ) }
			onRequestClose={ onRequestClose }
		>
			<div className="block-editor-block-patterns-explorer">
				<PatternExplorerSidebar
					selectedCategory={ selectedCategory }
					patternCategories={ patternCategories }
					onClickCategory={ setSelectedCategory }
					searchValue={ searchValue }
					setSearchValue={ setSearchValue }
				/>
				<div className="block-editor-block-patterns-explorer__list">
					<BlockContextProvider value={ { postType: 'popper' } }>
						{ patterns ? (
							<PatternsList
								blockPatterns={ shownPatterns }
								shownPatterns={ shownPatterns }
								clientId={ clientId }
								onRequestClose={ onRequestClose }
							/>
						) : (
							<Spinner />
						) }
					</BlockContextProvider>
				</div>
			</div>
		</Modal>
	);
}
